// Pure helpers + types for the Messages page "Channels" tab (read-only
// MeshCore channel messages). No imports, so the node test runner can load
// the compiled module directly.

export interface ChannelSummary {
  name: string | null;
  channel_hash: string;
  readable: boolean;
  count_24h: number;
  per_hour: number;
  last_message_at: string | null;
  // Set only for a name the scanner found (not one seen in traffic or set in config).
  found_source?: string | null;
  found_in?: string | null;
  found_at?: string | null;
}

export interface ChannelList {
  window_hours: number;
  retention_days?: number;
  channels: ChannelSummary[];
}

/** Region scope of a message, decoded from its own packet. `route` is null until the server has scanned it. */
export interface ChannelScope {
  route?: "unscoped_flood" | "scoped_flood" | "direct" | "mixed" | null;
  scope_name?: string | null; // "#imw"; "mixed" = receptions named different regions
  scope_match?: "unique" | "ambiguous" | "none" | null;
}

export interface ChannelMessage extends ChannelScope {
  packet_hash: string;
  heard_at: string;
  sender: string | null;
  text: string | null;
  sender_timestamp: number | null;
  observer_count: number;
}

export interface ChannelScopeOption {
  key: string; // value for the server's ?scope= filter
  label: string;
  count: number; // messages still stored (kept for the chat message retention setting)
  per_hour?: number; // messages per hour over the last 24 h
}

export interface ChannelMessagePage {
  channel: string;
  messages: ChannelMessage[];
  next_before: string | null;
  scopes?: ChannelScopeOption[] | null; // scoped options seen in this channel (first page only)
  scope_totals?: ChannelScopeTotals | null; // counts for "All scopes" and "Unscoped" (first page only)
}

export interface ChannelScopeTotals {
  all: number;
  unscoped: number;
  unscoped_per_hour?: number;
}

export interface ScopePill {
  label: string;
  tip: string;
  muted: boolean;
  region: boolean; // a region scope (drawn with a region marker), as opposed to "unscoped" / "direct"
}

/** The small pill after the sender: the region name, "unscoped" (muted), or "scope ?". Null = not scanned yet. */
export function scopePill(m: ChannelScope): ScopePill | null {
  if (!m.route) return null;
  if (m.route === "unscoped_flood") {
    return { label: "unscoped", tip: "Sent to everyone in range, not restricted to a region.", muted: true, region: false };
  }
  if (m.route === "direct") {
    return { label: "direct", tip: "Sent along a set path, not flooded to a region.", muted: true, region: false };
  }
  if (m.route === "mixed" || m.scope_name === "mixed") {
    return { label: "mixed", tip: "The copies we heard disagree about the region this was sent to.", muted: false, region: true };
  }
  if (m.scope_name) {
    return { label: m.scope_name, tip: `Region scope ${m.scope_name}: sent only to that region. This is not a channel name.`, muted: false, region: true };
  }
  if (m.scope_match === "ambiguous") {
    return { label: "scope ?", tip: "Scoped, but more than one known region name fits.", muted: false, region: true };
  }
  return { label: "scope ?", tip: "Scoped, but the region name isn't known.", muted: false, region: true };
}

/** Dropdown options above the message list: all, unscoped, then each scope seen in the channel, each with its count. */
export function scopeOptions(
  seen: ChannelScopeOption[] | null | undefined,
  totals?: ChannelScopeTotals | null,
): { key: string; label: string }[] {
  const n = (v: number | undefined) => (v === undefined ? "" : ` (${v})`);
  return [
    { key: "", label: `All scopes${n(totals?.all)}` },
    { key: "unscoped", label: `Unscoped${n(totals?.unscoped)}` },
    ...(seen ?? []).map((o) => ({ key: o.key, label: `${o.label} (${o.count})` })),
  ];
}

/** The selected scope's name for the channel header ("#imw", "unscoped"). */
export function scopeFilterName(key: string, seen: ChannelScopeOption[] | null | undefined): string {
  if (key === "unscoped") return "unscoped";
  return seen?.find((o) => o.key === key)?.label ?? key.replace(/^named:/, "");
}

/** Messages per hour (last 24 h) of the selected scope; null when it is not known (yet). */
export function scopeFilterRate(
  key: string,
  seen: ChannelScopeOption[] | null | undefined,
  totals?: ChannelScopeTotals | null,
): number | null {
  if (!key) return null;
  if (key === "unscoped") return totals?.unscoped_per_hour ?? null;
  return seen?.find((o) => o.key === key)?.per_hour ?? null;
}

/** Tooltip for the scope counts: how far back stored messages go (retention.chat_messages_days). */
export function scopeCountsTip(retentionDays: number | null | undefined): string {
  if (!retentionDays || retentionDays < 1) return "messages stored";
  return `messages in the last ${retentionDays} ${retentionDays === 1 ? "day" : "days"}`;
}
export const SCOPE_ROW_TIP = "The region this message was sent to, if it was sent to one.";
export const FOUND_MARK_TIP = "This channel's name was found by the server's name scanner.";

export interface ChannelHop {
  hash: string;
  status: "unique" | "path" | "ambiguous" | "none" | "impossible";
  n_candidates: number;
  /** Path-resolved hops only: how many nodes the short ID alone matched. */
  n_short_id?: number;
  node_id: string | null;
  name: string | null;
}

export interface ChannelObserver {
  observer_id: string;
  name: string | null;
  source: "corescope" | "own_radio";
  heard_at: string | null;
  snr: number | null;
  rssi: number | null;
  hops: ChannelHop[];
  hop_count: number;
  is_own?: boolean;
  copy_count?: number;
  copies?: ChannelCopy[];
}

export interface ChannelCopy {
  heard_at: string | null;
  snr: number | null;
  rssi: number | null;
}

export interface ChannelMessageDetail extends ChannelScope {
  packet_hash: string;
  channel_name: string;
  channel_hash: string;
  sender: string | null;
  text: string | null;
  heard_at: string;
  observer_count: number;
  observers: ChannelObserver[];
}

/** "12.3/h", "0.4/h", "quiet" for a zero. */
export function rateLabel(perHour: number): string {
  if (!(perHour > 0)) return "quiet";
  return `${perHour >= 10 ? Math.round(perHour) : perHour.toFixed(1)}/h`;
}

/** Row label for a channel in the list. */
export function channelLabel(c: ChannelSummary): string {
  if (c.readable && c.name) return c.name;
  return `Unknown channel (hash ${c.channel_hash.toUpperCase()})`;
}

export const RATE_TIP = "messages per hour, last 24 h";
export const QUIET_TIP = "no messages in the last 24 h";

export function rateTip(perHour: number): string {
  return perHour > 0 ? RATE_TIP : QUIET_TIP;
}

/** The two real channels that are easy to mix up. */
export function publicChannelTip(name: string | null): string | null {
  if (name === "Public") return "The standard Public channel, using the key every MeshCore radio ships with. Not the same channel as #public.";
  if (name && name.toLowerCase() === "#public") return "The hashtag channel #public, whose key comes from its name. Not the same channel as the standard Public.";
  return null;
}

export const UNKNOWN_SHOWN_DEFAULT = 5;

/** Unreadable channels to list: the busiest few, unless everything is asked for. */
export function visibleUnreadable(all: ChannelSummary[], showAll: boolean): ChannelSummary[] {
  const sorted = [...all].sort((a, b) => b.per_hour - a.per_hour || a.channel_hash.localeCompare(b.channel_hash));
  if (showAll) return sorted;
  return sorted.filter((c) => c.per_hour > 0).slice(0, UNKNOWN_SHOWN_DEFAULT);
}

export function observerCountLabel(n: number): string {
  return `heard by ${n} ${n === 1 ? "observer" : "observers"}`;
}

/** A hop shown by name when our resolver found exactly one node, otherwise by
 * its short ID. */
export function hopLabel(h: ChannelHop): string {
  if ((h.status === "unique" || h.status === "path") && h.name) return h.name;
  return h.hash.toUpperCase();
}

/** Short tag for a repeater that was named only through the path around it. */
export const PATH_TAG = "Resolved By Path";
export const PATH_TAG_TIP = "The short ID on its own matched several repeaters. The repeaters before and after it in the path, and distance, left only this one.";

/** The tag to show next to a hop, or null for a hop named by its ID alone. */
export function hopTag(h: ChannelHop): string | null {
  return h.status === "path" && h.node_id ? PATH_TAG : null;
}

/** What the small "Path" badge reads on the path line. */
export const PATH_BADGE = "Path";

/** The badge hover: the full label, with the candidate count when it is known. */
export function pathBadgeTip(candidates?: number | null): string {
  return candidates && candidates > 1 ? `${PATH_TAG} · ${candidates} Candidates` : PATH_TAG;
}

export function hopHint(h: ChannelHop): string | null {
  if (h.status === "ambiguous" || h.status === "impossible") return `ambiguous: ${h.n_candidates} candidates`;
  if (h.status === "none") return "unknown";
  if ((h.status === "unique" || h.status === "path") && !h.name) return `Matches one node (${h.node_id ?? "unnamed"}) that has no name.`;
  return null;
}

export function hopCountLabel(n: number): string {
  return `${n} ${n === 1 ? "hop" : "hops"}`;
}

/** A path as one comparable string. */
export function pathKey(o: ChannelObserver): string {
  return o.hops.map((h) => h.hash).join(">");
}

/** The path to show once above the table: the one at least two observers
 * share (or the only observer's), plus the observers whose path differs. */
export function splitPaths(observers: ChannelObserver[]): { common: ChannelObserver | null; differing: Set<ChannelObserver> } {
  const differing = new Set<ChannelObserver>();
  if (observers.length === 0) return { common: null, differing };
  const tally = new Map<string, { n: number; first: ChannelObserver }>();
  for (const o of observers) {
    const k = pathKey(o);
    const t = tally.get(k);
    if (t) t.n += 1;
    else tally.set(k, { n: 1, first: o });
  }
  let best: { n: number; first: ChannelObserver } | null = null;
  for (const t of tally.values()) if (!best || t.n > best.n) best = t;
  if (!best || (best.n < 2 && observers.length > 1)) {
    for (const o of observers) differing.add(o);
    return { common: null, differing };
  }
  const k = pathKey(best.first);
  for (const o of observers) if (pathKey(o) !== k) differing.add(o);
  return { common: best.first, differing };
}

/** "+0 s", "+2 s", "+3 min" -- when an observer heard it, relative to the first. */
export function offsetLabel(heardAt: string | null, firstAt: string | null): string {
  if (!heardAt || !firstAt) return "--";
  const s = Math.max(0, Math.round((Date.parse(heardAt) - Date.parse(firstAt)) / 1000));
  return s < 120 ? `+${s} s` : `+${Math.round(s / 60)} min`;
}

/** Local clock time with the zone abbreviation, e.g. "10:42:03 AM MDT". */
export function clockWithZone(ts: string | null | undefined): string {
  if (!ts) return "--";
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return "--";
  return new Date(t).toLocaleTimeString(undefined, { timeZoneName: "short" });
}

/** Local date and time with the zone abbreviation. */
export function dateTimeWithZone(ts: string | null | undefined): string {
  if (!ts) return "--";
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return "--";
  return new Date(t).toLocaleString(undefined, { timeZoneName: "short" });
}

export function rssiLabel(rssi: number | null | undefined): string {
  return rssi === null || rssi === undefined ? "--" : `${Math.round(rssi)} dBm`;
}

/** Hover text listing each copy an observer received. */
export function copiesTip(copies: ChannelCopy[]): string {
  return copies
    .map((c) => `${clockWithZone(c.heard_at)} · ${snrLabel(c.snr)}${c.rssi != null ? ` · ${rssiLabel(c.rssi)}` : ""}`)
    .join("; ");
}

export function snrLabel(snr: number | null): string {
  return snr === null || snr === undefined ? "--" : `${snr.toFixed(1)} dB`;
}

/** Merge a freshly fetched newest page into the list already on screen,
 * keeping order (newest first) and dropping duplicates. */
export function mergeNewest(existing: ChannelMessage[], fresh: ChannelMessage[]): ChannelMessage[] {
  const seen = new Set(existing.map((m) => m.packet_hash));
  const added = fresh.filter((m) => !seen.has(m.packet_hash));
  const updated = new Map(fresh.map((m) => [m.packet_hash, m]));
  return [...added, ...existing.map((m) => updated.get(m.packet_hash) ?? m)];
}

// --- Channel names we hold keys for, and where each came from -------------

export interface KnownChannelName {
  name: string;
  verified_on?: number; // stored messages this name decrypts
  channel_hash: string;
  source: string; // config | seen | common list | mentioned | region | node name
  found_in: string | null;
  found_at: string | null;
  removable: boolean;
}

export interface KnownChannelNames {
  names: KnownChannelName[];
  removed: string[];
}

/** Plain-words origin of a known name. */
export function nameSourceLabel(source: string, foundIn?: string | null): string {
  switch (source) {
    case "config": return "Set in your settings";
    case "seen": return "Seen in traffic";
    case "common list": return "On the list of common channel names";
    case "mentioned": return foundIn ? `Mentioned in ${foundIn}` : "Mentioned in a message";
    case "region": return "A region name from your settings";
    case "node name": return "A word from a node name";
    default: return source;
  }
}

/** Date part (YYYY-MM-DD) of an ISO time, or "" when there is none. */
export function foundDate(ts: string | null | undefined): string {
  return ts && /^\d{4}-\d{2}-\d{2}/.test(ts) ? ts.slice(0, 10) : "";
}

/** Hover text for a channel the scanner found; null for every other channel. */
export function foundByTip(c: { found_source?: string | null; found_in?: string | null; found_at?: string | null }): string | null {
  if (!c.found_source) return null;
  const d = foundDate(c.found_at);
  return `Found by: ${nameSourceLabel(c.found_source, c.found_in).replace(/^./, (x) => x.toLowerCase())}${d ? ` on ${d}` : ""}`;
}

export const CHANNEL_NAMES_INTRO =
  "A hashtag channel's key comes from its name, so a name we know lets this server read that channel. " +
  "The server looks for names now and then and keeps one only after it checks out on at least 3 stored messages. " +
  "Private channels with a secret key stay unreadable.";
export const REMOVE_NAME_TIP =
  "Stops using this name. Messages read with it go back to unreadable, and the server won't add this name again unless you allow it.";

export const DISCOVERED_SOURCES = ["mentioned", "region", "node name", "common list"];

/** "verified on 12 messages" for a discovered name. */
export function verifiedLabel(n: number | undefined): string {
  if (n === undefined) return "";
  return `verified on ${n} ${n === 1 ? "message" : "messages"}`;
}

export const NOT_NEEDED_TIP = "the name is known, so no check is needed";

export interface NameGroup {
  key: string; // "" for the first group (settings + traffic), else the discovery source
  names: KnownChannelName[];
}

/** Settings and seen-in-traffic names first, then discovered names grouped by source. */
export function groupKnownNames(names: KnownChannelName[]): NameGroup[] {
  const byName = (a: KnownChannelName, b: KnownChannelName) => a.name.localeCompare(b.name);
  const first = names.filter((n) => !n.removable).sort((a, b) => (a.source === b.source ? byName(a, b) : a.source === "config" ? -1 : 1));
  const out: NameGroup[] = [];
  if (first.length) out.push({ key: "", names: first });
  const rest = names.filter((n) => n.removable);
  const sources = [...DISCOVERED_SOURCES, ...[...new Set(rest.map((n) => n.source))].filter((s) => !DISCOVERED_SOURCES.includes(s))];
  for (const s of sources) {
    const g = rest.filter((n) => n.source === s).sort(byName);
    if (g.length) out.push({ key: s, names: g });
  }
  return out;
}

export function groupTitle(key: string): string {
  return key === "" ? "From settings and traffic" : `Found: ${nameSourceLabel(key).replace(/^./, (x) => x.toLowerCase())}`;
}

export const REMOVE_ALL_TIP =
  "Removes every name in this group. Their messages go back to unreadable, and the names go on the removed list below, where Allow again brings each back.";
export function removeAllConfirm(source: string, n: number): string {
  return `Remove all ${n} ${n === 1 ? "name" : "names"} found by this source (${nameSourceLabel(source).toLowerCase()})? Their messages go back to unreadable. You can bring any back with Allow again.`;
}
